// Plantillas de requisitos (GET/PUT /documents/requirements): la base de la
// plataforma por modo × dirección y las plantillas de la compañía (modo ×
// dirección × Incoterm) que sustituyen, agregan o quitan tipos y dicen quién
// los entrega. El resultado efectivo se previsualiza con la misma regla que el
// servidor (la plantilla más específica gana; `remove` quita de la base).
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge, Button, EmptyState, Switch, cn } from "../../../components/ui";
import { FormField, LoadBlock, Toolbar, inputCls } from "../../bases-propiedades/components/console";
import { DOCUMENT_DIRECTIONS, DOCUMENT_MODES, DOCUMENT_PROVIDERS, DOCUMENT_TYPES, DOCUMENT_TYPE_IDS, INCOTERMS, documentTypeName, type DocumentDirection, type DocumentMode, type DocumentProvider } from "../../../utils/documentTypes";
import { documentsErrorMessage, isTemplatesInvalidError, type DocumentRequirement, type RequirementTemplate, type TemplateItem } from "../../../services/shipmentDocuments";
import { CATEGORY_ICONS, DIRECTION_ICONS, DOCS_NS, MODE_ICONS, PROVIDER_ICONS } from "../../../features/shipmentDocuments/meta";
import { useCompanyRequirements, useSaveRequirements } from "../queries";
import { queryStatus } from "../components/pieces";

const templateKey = (t: Pick<RequirementTemplate, "mode" | "direction" | "incoterm">) => `${t.mode}|${t.direction}|${t.incoterm || "*"}`;
const isRemove = (it: TemplateItem): it is { doc_type: string; remove: true } => (it as { remove?: boolean }).remove === true;

/** Base + plantilla: mismo resultado que `mergeRequirements` del API. */
export function mergeRequirements(base: DocumentRequirement[], template: RequirementTemplate | null): DocumentRequirement[] {
  if (!template) return base;
  const out = new Map(base.map((r) => [r.doc_type, r]));
  for (const it of template.items) {
    if (isRemove(it)) out.delete(it.doc_type);
    else out.set(it.doc_type, { doc_type: it.doc_type, required: it.required !== false, provided_by: it.provided_by || DOCUMENT_TYPES[it.doc_type]?.provided_by || "cliente" });
  }
  const rank: Record<string, number> = { comercial: 0, transporte: 1, aduanal: 2, seguro: 3, sanitario: 4, interno: 5 };
  return [...out.values()].sort((a, b) => {
    const ca = rank[DOCUMENT_TYPES[a.doc_type]?.category ?? ""] ?? 9;
    const cb = rank[DOCUMENT_TYPES[b.doc_type]?.category ?? ""] ?? 9;
    if (ca !== cb) return ca - cb;
    if (a.required !== b.required) return a.required ? -1 : 1;
    return a.doc_type.localeCompare(b.doc_type);
  });
}

/** Claves repetidas (modo|dirección|incoterm) entre las plantillas. */
export function duplicateTemplateKeys(templates: RequirementTemplate[]): string[] {
  const seen = new Map<string, number>();
  for (const t of templates) seen.set(templateKey(t), (seen.get(templateKey(t)) ?? 0) + 1);
  return [...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k);
}

function RequirementRow({ r, lng }: { r: DocumentRequirement; lng: string }) {
  const { t } = useTranslation(DOCS_NS);
  const spec = DOCUMENT_TYPES[r.doc_type];
  return (
    <li className="flex items-center gap-2 py-1.5 text-sm">
      <i className={cn(spec ? CATEGORY_ICONS[spec.category] : "ri-file-line", "text-content-muted")} />
      <span className="min-w-0 flex-1 truncate text-content">{documentTypeName(r.doc_type, lng)}</span>
      <Badge size="sm" tone="neutral">
        <i className={PROVIDER_ICONS[r.provided_by]} /> {t(`provider.${r.provided_by}`)}
      </Badge>
      <Badge size="sm" tone={r.required ? "primary" : "neutral"}>
        {r.required ? t("common.required") : t("common.optional")}
      </Badge>
    </li>
  );
}

export default function PlantillasSection() {
  const { t, i18n } = useTranslation(DOCS_NS);
  const query = useCompanyRequirements();
  const save = useSaveRequirements();
  const qs = queryStatus(query);

  const [templates, setTemplates] = useState<RequirementTemplate[]>([]);
  const [dirty, setDirty] = useState(false);
  const [baseMode, setBaseMode] = useState<DocumentMode>("maritimo");
  const [baseDirection, setBaseDirection] = useState<DocumentDirection>("exportacion");
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (query.data && !dirty) setTemplates(query.data.company.map((tp) => ({ ...tp, items: [...tp.items] })));
  }, [query.data, dirty]);

  const baseFor = (mode: DocumentMode, direction: DocumentDirection): DocumentRequirement[] => query.data?.base.find((b) => b.mode === mode && b.direction === direction)?.items.filter((it): it is DocumentRequirement => !isRemove(it)) ?? [];
  const base = baseFor(baseMode, baseDirection);
  const duplicates = useMemo(() => duplicateTemplateKeys(templates), [templates]);
  // Misma regla que el API (422 `documentTemplatesInvalid`): ninguna plantilla
  // puede dejar al embarque sin documentos obligatorios.
  const withoutRequired = useMemo(() => templates.map((tp, i) => (mergeRequirements(baseFor(tp.mode, tp.direction), tp).some((r) => r.required) ? -1 : i)).filter((i) => i >= 0), [templates, query.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [apiError, setApiError] = useState<string | null>(null);

  const update = (i: number, patch: Partial<RequirementTemplate>) => {
    setTemplates((prev) => prev.map((tp, idx) => (idx === i ? { ...tp, ...patch } : tp)));
    setDirty(true);
  };
  const updateItem = (i: number, docType: string, next: TemplateItem | null) => {
    setTemplates((prev) =>
      prev.map((tp, idx) => {
        if (idx !== i) return tp;
        const items = tp.items.filter((it) => it.doc_type !== docType);
        return { ...tp, items: next ? [...items, next] : items };
      }),
    );
    setDirty(true);
  };
  const add = () => {
    setTemplates((prev) => [...prev, { mode: baseMode, direction: baseDirection, incoterm: null, items: [] }]);
    setActive(templates.length);
    setDirty(true);
  };
  const remove = (i: number) => {
    setTemplates((prev) => prev.filter((_, idx) => idx !== i));
    setActive(null);
    setDirty(true);
  };

  const persist = async () => {
    setApiError(null);
    try {
      await save.mutateAsync(templates);
      setDirty(false);
      toast.success(t("plantillas.saved"));
    } catch (err) {
      const msg = documentsErrorMessage(err, t("plantillas.saveFailed"));
      if (isTemplatesInvalidError(err)) setApiError(`${t("plantillas.rejected")}: ${msg}`);
      toast.error(msg);
    }
  };

  const lng = i18n.language;
  const modeOpts = DOCUMENT_MODES.map((m) => ({ value: m, label: t(`mode.${m}`) }));
  const dirOpts = DOCUMENT_DIRECTIONS.map((d) => ({ value: d, label: t(`direction.${d}`) }));

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Toolbar>
        <div className="flex items-center gap-2">
          <i className="ri-list-settings-line text-lg text-primary-500" />
          <h1 className="text-sm font-semibold text-content">{t("plantillas.title")}</h1>
          {dirty ? (
            <Badge size="sm" tone="warning" dot>
              {t("plantillas.unsaved")}
            </Badge>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="secondary" icon={<i className="ri-add-line" />} onClick={add} disabled={query.isLoading}>
            {t("plantillas.newTemplate")}
          </Button>
          <Button size="sm" icon={<i className="ri-save-line" />} loading={save.isPending} disabled={!dirty || duplicates.length > 0 || withoutRequired.length > 0} onClick={() => void persist()}>
            {t("plantillas.save")}
          </Button>
        </div>
      </Toolbar>
      <div className="flex-1 overflow-y-auto p-4">
        <LoadBlock status={qs.status} error={qs.error} onRetry={qs.retry} empty={false} emptyTitle="">
          <p className="mb-4 max-w-3xl text-xs text-content-secondary">{t("plantillas.intro")}</p>
          {duplicates.length ? (
            <p role="alert" className="mb-3 rounded-lg border border-error/40 bg-error/5 px-3 py-2 text-xs text-error">
              {t("plantillas.duplicate")}: {duplicates.join(", ")}
            </p>
          ) : null}
          {withoutRequired.length ? (
            <p role="alert" className="mb-3 rounded-lg border border-error/40 bg-error/5 px-3 py-2 text-xs text-error" data-testid="templates-no-required">
              {t("plantillas.noRequired")} ({withoutRequired.map((i) => templateKey(templates[i])).join(", ")})
            </p>
          ) : null}
          {apiError ? (
            <p role="alert" className="mb-3 rounded-lg border border-error/40 bg-error/5 px-3 py-2 text-xs text-error">
              {apiError}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[22rem_1fr]">
            {/* Base de la plataforma */}
            <section className="rounded-xl border border-line bg-card p-4" aria-labelledby="base-title">
              <h2 id="base-title" className="mb-3 text-sm font-semibold text-content">
                {t("plantillas.baseTitle")}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                <FormField label={t("plantillas.mode")}>
                  <select value={baseMode} onChange={(e) => setBaseMode(e.target.value as DocumentMode)} className={inputCls}>
                    {modeOpts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label={t("plantillas.direction")}>
                  <select value={baseDirection} onChange={(e) => setBaseDirection(e.target.value as DocumentDirection)} className={inputCls}>
                    {dirOpts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-content-muted">
                <i className={MODE_ICONS[baseMode]} /> <i className={DIRECTION_ICONS[baseDirection]} /> {t("plantillas.baseHint", { mode: t(`mode.${baseMode}`), direction: t(`direction.${baseDirection}`) })} · {t("plantillas.baseCount", { n: base.length })}
              </div>
              <ul className="mt-2 divide-y divide-line" data-testid="base-requirements">
                {base.map((r) => (
                  <RequirementRow key={r.doc_type} r={r} lng={lng} />
                ))}
              </ul>
            </section>

            {/* Plantillas de la compañía */}
            <section className="space-y-3" aria-labelledby="company-title">
              <h2 id="company-title" className="text-sm font-semibold text-content">
                {t("plantillas.companyTitle")}
              </h2>
              {templates.length === 0 ? (
                <EmptyState icon="ri-list-settings-line" title={t("plantillas.empty")} description={t("plantillas.emptyText")} action={<Button variant="secondary" icon={<i className="ri-add-line" />} onClick={add}>{t("plantillas.newTemplate")}</Button>} />
              ) : (
                templates.map((tp, i) => {
                  const tpBase = baseFor(tp.mode, tp.direction);
                  const effective = mergeRequirements(tpBase, tp);
                  const present = new Set(tp.items.map((it) => it.doc_type));
                  const addable = DOCUMENT_TYPE_IDS.filter((id) => !present.has(id) && DOCUMENT_TYPES[id].modes.includes(tp.mode));
                  const removable = tpBase.filter((r) => !present.has(r.doc_type));
                  const open = active === i;
                  const dup = duplicates.includes(templateKey(tp)) || withoutRequired.includes(i);
                  return (
                    <article key={i} className={cn("rounded-xl border bg-card", dup ? "border-error" : "border-line")} data-testid="company-template">
                      <header className="flex flex-wrap items-center gap-2 px-4 py-3">
                        <button type="button" onClick={() => setActive(open ? null : i)} aria-expanded={open} className="flex items-center gap-2 text-left text-sm font-semibold text-content">
                          <i className={open ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
                          <i className={MODE_ICONS[tp.mode]} /> {t(`mode.${tp.mode}`)} · {t(`direction.${tp.direction}`)} · {tp.incoterm || t("plantillas.anyIncoterm")}
                        </button>
                        <span className="text-xs text-content-muted">
                          {tp.items.length} {t("plantillas.items").toLowerCase()}
                        </span>
                        <div className="ml-auto flex items-center gap-1">
                          <Button size="xs" variant="ghost" icon={<i className="ri-delete-bin-line" />} onClick={() => remove(i)} aria-label={t("plantillas.delete")} title={t("plantillas.delete")} iconOnly />
                        </div>
                      </header>
                      {open ? (
                        <div className="space-y-3 border-t border-line px-4 py-3">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <FormField label={t("plantillas.mode")}>
                              <select value={tp.mode} onChange={(e) => update(i, { mode: e.target.value as DocumentMode, items: tp.items.filter((it) => DOCUMENT_TYPES[it.doc_type]?.modes.includes(e.target.value as DocumentMode)) })} className={inputCls}>
                                {modeOpts.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </FormField>
                            <FormField label={t("plantillas.direction")}>
                              <select value={tp.direction} onChange={(e) => update(i, { direction: e.target.value as DocumentDirection })} className={inputCls}>
                                {dirOpts.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </FormField>
                            <FormField label={t("plantillas.incoterm")}>
                              <select value={tp.incoterm ?? ""} onChange={(e) => update(i, { incoterm: e.target.value || null })} className={inputCls}>
                                <option value="">{t("plantillas.anyIncoterm")}</option>
                                {INCOTERMS.map((ic) => (
                                  <option key={ic} value={ic}>
                                    {ic}
                                  </option>
                                ))}
                              </select>
                            </FormField>
                          </div>

                          <div>
                            <div className="mb-1 text-xs font-medium text-content-secondary">{t("plantillas.items")}</div>
                            {tp.items.length === 0 ? <p className="text-xs text-content-muted">—</p> : null}
                            <ul className="divide-y divide-line rounded-lg border border-line">
                              {tp.items.map((it) => (
                                <li key={it.doc_type} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                                  <span className="min-w-0 flex-1 truncate text-content">{documentTypeName(it.doc_type, lng)}</span>
                                  {isRemove(it) ? (
                                    <>
                                      <Badge size="sm" tone="error">
                                        {t("plantillas.removedType")}
                                      </Badge>
                                      <Button size="xs" variant="ghost" onClick={() => updateItem(i, it.doc_type, null)}>
                                        {t("plantillas.restore")}
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <label className="flex items-center gap-1.5 text-xs text-content-secondary">
                                        {t("plantillas.required")}
                                        <Switch size="sm" checked={it.required} onChange={(v) => updateItem(i, it.doc_type, { ...it, required: v })} aria-label={`${t("plantillas.required")}: ${documentTypeName(it.doc_type, lng)}`} />
                                      </label>
                                      <select aria-label={`${t("plantillas.providedBy")}: ${documentTypeName(it.doc_type, lng)}`} value={it.provided_by} onChange={(e) => updateItem(i, it.doc_type, { ...it, provided_by: e.target.value as DocumentProvider })} className="rounded-lg border border-line bg-app px-2 py-1 text-xs text-content outline-none focus:border-primary-500">
                                        {DOCUMENT_PROVIDERS.map((p) => (
                                          <option key={p} value={p}>
                                            {t(`provider.${p}`)}
                                          </option>
                                        ))}
                                      </select>
                                      <button type="button" onClick={() => updateItem(i, it.doc_type, null)} aria-label={`${t("plantillas.remove")}: ${documentTypeName(it.doc_type, lng)}`} title={t("plantillas.remove")} className="flex h-7 w-7 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle hover:text-error">
                                        <i className="ri-close-line" />
                                      </button>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <select
                                aria-label={t("plantillas.addType")}
                                value=""
                                onChange={(e) => {
                                  const id = e.target.value;
                                  if (!id) return;
                                  updateItem(i, id, { doc_type: id, required: true, provided_by: DOCUMENT_TYPES[id].provided_by });
                                }}
                                className={inputCls}
                              >
                                <option value="">{t("plantillas.addType")}…</option>
                                {addable.map((id) => (
                                  <option key={id} value={id}>
                                    {documentTypeName(id, lng)}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={t("plantillas.removeFromBase")}
                                value=""
                                onChange={(e) => {
                                  const id = e.target.value;
                                  if (!id) return;
                                  updateItem(i, id, { doc_type: id, remove: true });
                                }}
                                className={inputCls}
                              >
                                <option value="">{t("plantillas.removeFromBase")}…</option>
                                {removable.map((r) => (
                                  <option key={r.doc_type} value={r.doc_type}>
                                    {documentTypeName(r.doc_type, lng)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <details className="rounded-lg border border-line bg-app px-3 py-2">
                            <summary className="cursor-pointer text-xs font-medium text-content-secondary">
                              {t("plantillas.effective")} · {t("plantillas.baseCount", { n: effective.length })}
                            </summary>
                            <ul className="mt-1 divide-y divide-line">
                              {effective.map((r) => (
                                <RequirementRow key={r.doc_type} r={r} lng={lng} />
                              ))}
                            </ul>
                          </details>
                        </div>
                      ) : null}
                    </article>
                  );
                })
              )}
            </section>
          </div>
        </LoadBlock>
      </div>
    </div>
  );
}
